"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CircleQuestionMark as CircleHelp, ArrowRight } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
interface FaqItem {
  id: string;
  num: string;
  title: string;
  content: string;
}
const faqItems: FaqItem[] = [{
  id: "item-1",
  num: "01",
  title: "Comida e Bebidas",
  content: "Nas linhas de processamento alimentar, enchimento e engarrafamento, os sistemas Eurocontrol e Spraying Systems Co. aplicam com precisão lubrificantes certificados NSF H1 em correntes de transporte e componentes móveis. A dosagem micrométrica previne contaminações cruzadas, reduz o desperdício de fluido e assegura máxima fiabilidade operacional mesmo sob frequentes lavagens a alta pressão e ambientes de humidade contínua."
}, {
  id: "item-2",
  num: "02",
  title: "Metais",
  content: "Em operações de estampagem, laminagem, corte e maquinação, a pulverização homogénea de lubrificantes e óleos de deformação suporta regimes severos de temperatura e extrema pressão. A tecnologia de bicos automáticos reduz o atrito pontual, minimiza o desgaste prematuro de ferramentas e matrizes e prolonga de forma mensurável o ciclo de vida dos equipamentos industriais."
}, {
  id: "item-3",
  num: "03",
  title: "Engenharia de Madeiras",
  content: "Na transformação de madeira, aglomerados e linhas de prensagem contínua, os sistemas de lubrificação resistem à acumulação severa de serradura e partículas abrasivas em suspensão. O controlo de jato direcionado protege serras, guias e correntes de colagem, evitando paragens não programadas causadas por resíduos acumulados e sobreaquecimento mecânico."
}, {
  id: "item-4",
  num: "04",
  title: "Produção de Latas",
  content: "Nas linhas de embalagens metálicas e produção de latas de alta velocidade, a lubrificação precisa de correntes transportadoras e guias elimina o atrito mecânico e reduz a emissão de ruído fabril. O controlo eletrónico de caudal acompanha variações de cadência em tempo real, garantindo aplicação constante sem névoa excessiva nem manchas nos produtos."
}, {
  id: "item-5",
  num: "05",
  title: "Veículos",
  content: "Na montagem automóvel e fabrico de componentes, a precisão da dosagem de óleos protetores e fluidos de montagem em transportadores aéreos e prensas robotizadas assegura repetibilidade absoluta. Reduz tempos de manutenção preventiva e elimina desvios de processo em ambientes fabris de grande escala com suporte técnico Eurocontrol."
}];
export default function IndustryFaq() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="industryfaq" data-nav="light" className="bg-background text-foreground relative py-16 md:py-24">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16">
          {/* Left Column: Sticky Overview & Support CTA */}
          <motion.div initial={{
          opacity: 0,
          y: shouldReduceMotion ? 0 : 24
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.2
        }} transition={{
          duration: 0.6,
          ease: "easeOut"
        }} className="lg:col-span-5">
            <div className="lg:sticky lg:top-28 space-y-6">
              <div className="space-y-4">
                <Badge variant="outline" className="bg-accent text-accent-foreground border-border px-3 py-1 text-xs font-semibold uppercase tracking-wider gap-1.5">
                  <CircleHelp className="size-3.5" />
                  FAQ
                </Badge>

                <h2 className="text-3xl font-semibold tracking-tight sm:text-4xl text-foreground font-default">
                  Aplicações por Indústria
                </h2>

                <p className="text-muted-foreground text-base leading-relaxed">
                  Conheça a aplicação dos sistemas de precisão da Eurocontrol e Spraying Systems Co. nos principais setores industriais. Soluções concebidas para elevar a eficiência, poupar recursos e proteger os seus equipamentos.
                </p>
              </div>

              <Card className="border-border bg-card shadow-xs rounded-2xl p-6 sm:p-7">
                <CardHeader className="p-0 space-y-2">
                  <CardTitle className="text-lg font-semibold text-card-foreground">
                    Necessita de suporte técnico específico?
                  </CardTitle>
                  <CardDescription className="text-muted-foreground text-sm leading-normal">
                    Os nossos engenheiros analisam o seu processo produtivo para dimensionar a solução de lubrificação e pulverização mais adequada.
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-0 pt-6">
                  <Button asChild className="w-full sm:w-auto">
                    <Link to="/ContactsPage" className="inline-flex items-center justify-center gap-2">
                      <span>Consulte-nos</span>
                      <ArrowRight className="size-4" />
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            </div>
          </motion.div>

          {/* Right Column: Interactive Accordion */}
          <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.15
        }} className="lg:col-span-7">
            <Accordion type="single" collapsible defaultValue="item-1" className="space-y-4">
              {faqItems.map((item, i) => <motion.div key={item.id} data-index={i} variants={itemVariants} className="w-full">
                  <AccordionItem value={item.id} className="border border-border bg-card rounded-xl px-5 sm:px-6 transition-[border-color,box-shadow] hover:border-border/80 shadow-xs overflow-hidden">
                    <AccordionTrigger className="py-5 hover:no-underline text-left group">
                      <div className="flex items-center gap-3 sm:gap-4 pr-3">
                        <span className="flex items-center justify-center size-8 rounded-lg bg-accent text-accent-foreground text-xs font-mono font-semibold shrink-0">
                          {item.num}
                        </span>
                        <span className="text-base sm:text-lg font-semibold text-foreground group-hover:text-primary transition-colors">
                          {item.title}
                        </span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="pt-1 pb-6 text-muted-foreground text-sm sm:text-base leading-relaxed pl-11 sm:pl-12">
                      {item.content}
                    </AccordionContent>
                  </AccordionItem>
                </motion.div>)}
            </Accordion>
          </motion.div>
        </div>
      </div>
    </section>;
}